/* Admin · Team Notifications page
 *
 * Single-screen management for the verqo internal team's notification
 * routing. Two halves:
 *   1. Members table — add/edit/remove + Send Test
 *   2. Event routing matrix — checkbox grid: rows = events, cols = members
 *
 * Persistence: every change hits the backend immediately (no save button).
 * Matrix changes use a per-member PATCH so concurrent edits don't clobber.
 */
import { useEffect, useState } from "react";
import {
  Users, Plus, Trash2, Mail, MessageSquare, Send, Check,
  ShieldCheck, Loader2, AlertTriangle, X,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const ROLES = [
  { v: "founder",  label: "Founder" },
  { v: "ops",      label: "Operations" },
  { v: "support",  label: "Support" },
  { v: "finance",  label: "Finance" },
  { v: "engineer", label: "Engineering" },
  { v: "other",    label: "Other" },
];

export default function AdminTeamNotifications() {
  const [members, setMembers]     = useState([]);
  const [events, setEvents]       = useState([]);
  // prefs is keyed by `${member_id}::${event_type}` for fast lookup
  const [prefs, setPrefs]         = useState({});
  const [loading, setLoading]     = useState(true);
  const [addOpen, setAddOpen]     = useState(false);
  const [sendingTest, setSendingTest] = useState(null); // member_id while in-flight

  const reload = async () => {
    setLoading(true);
    try {
      const [m, e] = await Promise.all([
        api.get("/admin/team/members"),
        api.get("/admin/team/event-types"),
      ]);
      const memberList = m.data?.members || [];
      const eventList  = e.data?.events  || [];
      setMembers(memberList);
      setEvents(eventList);
      // Fetch prefs for every active+inactive member in parallel
      const allPrefs = await Promise.all(memberList.map(mb =>
        api.get(`/admin/team/members/${mb.id}/prefs`).then(r => r.data?.prefs || [])
      ));
      const map = {};
      allPrefs.forEach((rows, i) => {
        rows.forEach(p => {
          map[`${memberList[i].id}::${p.event_type}`] = { email: !!p.email, sms: !!p.sms };
        });
      });
      setPrefs(map);
    } catch (e) {
      toast.error("Couldn't load team notifications");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const togglePref = async (memberId, eventType, channel, value) => {
    // Optimistic update
    const key = `${memberId}::${eventType}`;
    const prev = prefs[key] || { email: false, sms: false };
    const next = { ...prev, [channel]: value };
    setPrefs(p => ({ ...p, [key]: next }));
    try {
      // We send the FULL row (both email AND sms) to avoid partial updates
      await api.patch(`/admin/team/members/${memberId}/prefs`, {
        prefs: [{ event_type: eventType, email: next.email, sms: next.sms }],
      });
    } catch (e) {
      // Roll back on failure
      setPrefs(p => ({ ...p, [key]: prev }));
      toast.error("Couldn't save preference");
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} (${m.email}) from the team?`)) return;
    try {
      await api.delete(`/admin/team/members/${m.id}`);
      toast.success(`Removed ${m.name}`);
      reload();
    } catch (e) {
      toast.error("Couldn't remove member");
    }
  };

  const sendTest = async (m) => {
    setSendingTest(m.id);
    try {
      await api.post(`/admin/team/members/${m.id}/send-test`);
      toast.success(`Test email sent to ${m.email}`);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Test email failed";
      toast.error(msg);
    } finally { setSendingTest(null); }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/admin/team/members/${m.id}`, { active: !m.active });
      setMembers(arr => arr.map(x => x.id === m.id ? { ...x, active: !m.active } : x));
    } catch (e) {
      toast.error("Couldn't update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-foreground/55 gap-2">
        <Loader2 className="w-4 h-4 animate-spin"/>Loading team notifications…
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-6 md:px-8 py-6 space-y-6" data-testid="admin-team-notifications">
      {/* Header */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Admin · Settings</div>
        <h1 className="display text-2xl mt-1 tracking-tight">Team notifications</h1>
        <p className="mt-1 text-[13px] text-foreground/65 max-w-2xl leading-relaxed">
          Pick which verqo team members receive emails for which platform events.
          Members don't need verqo admin logins — anyone you add here just gets
          the email. If nobody is subscribed to an event, it falls back to{" "}
          <span className="font-mono text-foreground/85">admin@traqo.com</span>.
        </p>
      </div>

      {/* Members table */}
      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-foreground/55"/>
            <span className="text-[13px] font-medium">Team members ({members.length})</span>
          </div>
          <Button size="sm" className="rounded-full bg-foreground text-background" onClick={() => setAddOpen(true)} data-testid="add-member">
            <Plus className="w-3.5 h-3.5 mr-1"/>Add member
          </Button>
        </div>
        {members.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-foreground/55">
            No team members yet. New alerts will be sent to <span className="mono">admin@traqo.com</span> until you add someone.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {members.map(m => (
              <div key={m.id} className={`grid grid-cols-[2fr_1fr_140px_140px_120px] gap-3 px-5 py-3 items-center ${m.active ? "" : "opacity-50"}`} data-testid={`member-row-${m.id}`}>
                <div>
                  <div className="text-[13px] font-medium text-foreground/85">{m.name}</div>
                  <div className="text-[11.5px] text-foreground/55">{m.email}</div>
                </div>
                <div className="text-[11.5px] text-foreground/65 mono uppercase tracking-wider">{m.role}</div>
                <div>
                  <button onClick={() => toggleActive(m)} className="inline-flex items-center gap-1 text-[11px]" data-testid={`toggle-${m.id}`}>
                    {m.active
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-700 border border-emerald-500/30"><Check className="w-2.5 h-2.5"/>Active</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-foreground/10 text-foreground/55 border border-foreground/15">Inactive</span>}
                  </button>
                </div>
                <div>
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]"
                          disabled={sendingTest === m.id || !m.active}
                          onClick={() => sendTest(m)}
                          data-testid={`test-${m.id}`}>
                    {sendingTest === m.id
                      ? <><Loader2 className="w-3 h-3 mr-1 animate-spin"/>Sending…</>
                      : <><Send className="w-3 h-3 mr-1"/>Test</>}
                  </Button>
                </div>
                <div className="text-right">
                  <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50 h-7 text-[11px]"
                          onClick={() => removeMember(m)} data-testid={`remove-${m.id}`}>
                    <Trash2 className="w-3 h-3 mr-1"/>Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Event routing matrix */}
      {members.length > 0 && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-foreground/55"/>
              <span className="text-[13px] font-medium">Event routing</span>
            </div>
            <div className="text-[11px] text-foreground/55">
              Check the box for each member who should receive emails when that event fires. SMS column appears when phone numbers are wired (Phase 2).
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[10px] mono uppercase tracking-wider text-foreground/55 border-b border-border">
                <tr>
                  <th className="text-left px-5 py-2 sticky left-0 bg-white">Event</th>
                  {members.map(m => (
                    <th key={m.id} className="px-3 py-2 text-center min-w-[110px]">
                      <div className="font-semibold text-foreground/80 normal-case tracking-normal">{m.name.split(" ")[0]}</div>
                      <div className="text-[9.5px] text-foreground/50 mono mt-0.5">{m.role}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map(ev => (
                  <tr key={ev.id} data-testid={`event-row-${ev.id}`} className="hover:bg-secondary/30">
                    <td className="px-5 py-2.5 sticky left-0 bg-white">
                      <div className="font-medium text-foreground/85">{ev.label}</div>
                      <div className="text-[10.5px] text-foreground/55 max-w-md leading-snug">{ev.description}</div>
                    </td>
                    {members.map(m => {
                      const key = `${m.id}::${ev.id}`;
                      const cur = prefs[key] || { email: false, sms: false };
                      return (
                        <td key={m.id} className="px-3 py-2 text-center">
                          <label className="inline-flex items-center gap-1 cursor-pointer select-none" title={`${m.name} - email`} data-testid={`pref-${m.id}-${ev.id}-email`}>
                            <input
                              type="checkbox"
                              checked={cur.email}
                              disabled={!m.active}
                              onChange={(e) => togglePref(m.id, ev.id, "email", e.target.checked)}
                              className="cursor-pointer disabled:cursor-not-allowed"
                            />
                            <Mail className="w-3 h-3 text-foreground/45"/>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AddMemberDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={reload}/>
    </div>
  );
}


function AddMemberDialog({ open, onClose, onAdded }) {
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState("ops");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);

  useEffect(() => {
    if (open) { setName(""); setEmail(""); setRole("ops"); setErr(null); }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/admin/team/members", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role,
        active: true,
      });
      toast.success(`Added ${name} to the team`);
      onAdded?.();
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || "Couldn't add member");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle className="display text-xl">Add team member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] mono uppercase tracking-widest text-foreground/55 block mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" data-testid="add-name"/>
          </div>
          <div>
            <label className="text-[10px] mono uppercase tracking-widest text-foreground/55 block mb-1">Email</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@verqo.com" data-testid="add-email"/>
          </div>
          <div>
            <label className="text-[10px] mono uppercase tracking-widest text-foreground/55 block mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full rounded border border-border bg-white px-3 py-2 text-[13px]" data-testid="add-role">
              {ROLES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>
          <div className="rounded-lg bg-secondary/40 border border-border p-2.5 text-[11.5px] text-foreground/65">
            Member starts with default subscriptions (signups, payments, fraud alerts).
            You can fine-tune after they're added.
          </div>
          {err && (
            <div className="rounded-lg border border-rose-300 bg-rose-50/50 p-2.5 text-[12px] text-rose-900 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>{err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !name.trim() || !email.trim()} className="bg-foreground text-background" data-testid="add-submit">
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Adding…</> : "Add member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
